import { Box, Button, Modal, Switch, Text, Tooltip } from "@mantine/core";
import { ILink } from "@mctypes/global.types";
import { ColDef } from "ag-grid-community";
import * as R from "ramda";
import React, { useEffect, useRef, useState } from "react";
import { HSpace, VSpace } from "../../../../components";
import ViewEditTable, { ViewEditTableRef } from "../../../../components/ViewEditTable/ViewEditTable";
import WidgetWrapper from "../../../../components/WidgetWrapper/WidgetWrapper";
import { setShowSuccessMessageThunk } from "../../../../global/globalSlice";
import { useAppDispatch, useAppSelector } from "../../../../hooks";
import { isDeepEqual, sortArrayByString } from "../../../utils";
import { useSaveLinksMutation } from "../ProjectDashboardRTK";
import { setLinksSortModeInRedux } from "../projectDashboardSlice";

const Links: React.FC = () => {
  const project = useAppSelector((state) => state.adminReducer.project);
  const activeLayout = useAppSelector((state) => state.projectDashboardReducer.activeLayout);
  const dispatch = useAppDispatch();

  const [editMode, setEditMode] = useState<boolean>(false);
  const [links, setLinks] = useState<ILink[]>([]);
  const [saveLinks] = useSaveLinksMutation();

  const sortMode = useAppSelector((state) => state.projectDashboardReducer.linksSortMode);

  const displayedLinks = sortMode === "color" ? sortArrayByString([...links], "icon") : links;

  useEffect(() => {
    if (activeLayout?.widgetData?.links != null) {
      setLinks(R.clone(activeLayout.widgetData.links));
    }
  }, [activeLayout]);

  const save = async (links: ILink[]) => {
    await saveLinks({ projectId: project._id, links: links, layoutId: activeLayout._id });
    setLinks(links);
    dispatch(setShowSuccessMessageThunk("Lenker lagret"));
    setEditMode(false);
  };

  const goToLink = (url) => {
    if (url.startsWith("www")) {
      window.open(`https://${url}`);
    } else {
      window.open(url);
    }
  };
  const LinkContainer = ({ link }) => {
    const [hover, setHover] = useState(false);
    if (link.icon === "") {
      link.icon = "#a1a2a2";
    }
    const linkContent = (
      <div
        onClick={() => goToLink(link.url)}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        className="newlink d-flex justify-content-between py-1"
        style={{
          boxShadow: `inset 16px 0px 0px -12px ${link.icon}`,
          background: `${link.icon}${hover ? "AA" : "50"}`,
          transition: ".2s ease-in-out background",
        }}
        key={link._id}
      >
        <div className="mc-gray-text text-truncate" style={{ paddingLeft: "4px", userSelect: "none" }}>
          {link.name}
        </div>
        {/* <i className="fa fa-external-link-square fa-sm fa-fw" /> */}
      </div>
    );

    if (link?.description?.length > 0) {
      return (
        <Tooltip label={link.description} openDelay={150} position="right" withArrow>
          {linkContent}
        </Tooltip>
      );
    }

    return linkContent;
  };
  return (
    <WidgetWrapper
      customTitle={
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
          <Text fz="md" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flexGrow: 1 }}>
            Lenker
          </Text>
          <Switch
            label="Sorter på farge"
            labelPosition="left"
            checked={sortMode === "color"}
            onChange={(e) => dispatch(setLinksSortModeInRedux(e.currentTarget.checked ? "color" : "manual"))}
          />
        </div>
      }
      widgetKey="links"
      onShowSettingsClick={() => setEditMode(!editMode)}
    >
      <div className="testflex">
        {displayedLinks.map((link, index) => (
          <LinkContainer link={link} key={link._id ?? index} />
        ))}
      </div>
      <EditLinksModal links={links} save={save} toggle={() => setEditMode(false)} isOpen={editMode} />
    </WidgetWrapper>
  );
};

export default Links;

const EditLinksModal: React.FC<{
  save: (links: ILink[]) => void;
  links: ILink[];
  toggle: () => void;
  isOpen: boolean;
}> = ({ save, links, toggle, isOpen }) => {
  const [editedLinks, setEditedLinks] = useState<ILink[] | null>(null);
  const vetRef = useRef<ViewEditTableRef<ILink>>(null);
  const [saveDisabled, setSaveDisabled] = useState<boolean>(true);

  useEffect(() => {
    setEditedLinks(R.clone(links));
  }, [links]);

  const linkColorOptions = [
    { color: "#e15353", label: "rød" },
    { color: "#ff9800", label: "oransje" },
    { color: "#94b0f3", label: "blå" },
    { color: "#80b180", label: "grønn" },
    { color: "#ffe499", label: "gul" },
    { color: "#a1a2a2", label: "grå" },
  ];

  const columns: ColDef<ILink>[] = [
    {
      field: "name",
      headerName: "Navn",
      editable: true,
      width: 220,
    },
    {
      field: "description",
      headerName: "Beskrivelse",
      editable: true,
      flex: 1,
    },
    {
      field: "url",
      headerName: "URL",
      editable: true,
      flex: 1,
      cellEditor: "agLargeTextCellEditor",
      cellEditorPopup: true,
      cellEditorParams: {
        maxLength: 1000, // eller 2048
        rows: 4,
        cols: 60,
      },
    },
    {
      field: "icon",
      headerName: "Farge",
      editable: true,
      width: 120,
      cellClass: "px-1",
      cellEditor: "agRichSelectCellEditor",
      cellEditorParams: {
        values: linkColorOptions.map((item) => item.color),
        searchType: "contains",
        cellRenderer: (params: any) => {
          const opt = linkColorOptions.find((o) => o.color === params.value);
          const color = opt?.color || "transparent";
          const label = opt?.label || params.value;
          return (
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span
                style={{
                  width: 14,
                  height: 14,
                  borderRadius: "50%",
                  background: color,
                  border: "1px solid #ccc",
                }}
              />
              <span style={{ fontSize: "12px" }}>{label}</span>
            </div>
          );
        },
        formatValue: (value: string) => {
          const opt = linkColorOptions.find((o) => o.color === value);
          return opt?.label || value;
        },
      },
      valueFormatter: (params) => {
        const opt = linkColorOptions.find((o) => o.color === params.value);
        return opt?.label || params.value;
      },
      cellRenderer: (params) => {
        const opt = linkColorOptions.find((o) => o.color === params.value);
        const color = opt?.color || "transparent";
        const label = opt?.label || params.value;
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span
              style={{
                width: 14,
                height: 14,
                borderRadius: "50%",
                background: color,
                border: "1px solid #ccc",
              }}
            ></span>
            <span style={{ fontSize: "0.75rem" }}>{label}</span>
          </div>
        );
      },
    },
  ];

  const newItemTemplateObject: ILink = {
    icon: "#a1a2a2",
    name: "",
    description: "",
    url: "",
  };

  const isDisabled = (rows: ILink[]) => {
    if (!rows || rows.length === 0) return true;

    const urlRegex = /^(https?:\/\/|www\.)[^\s/$.?#].[^\s]*$/i;

    const isValid = rows.every((link) => {
      const nameOk = typeof link?.name === "string" && link.name.trim().length > 0;
      const urlOk = typeof link?.url === "string" && urlRegex.test(link.url.trim());
      return nameOk && urlOk;
    });

    return !isValid;
  };
  // Initial compute when modal opens
  useEffect(() => {
    if (isOpen && editedLinks) {
      setSaveDisabled(isDisabled(editedLinks));
    }
  }, [isOpen, editedLinks]);
  const handleCloseModal = () => {
    const tableData = vetRef.current?.getData();
    const rows = (tableData?.rows as ILink[]) ?? editedLinks ?? [];
    const areEqual = isDeepEqual(links, rows);
    toggle();

    if (areEqual === false && isDisabled(rows) === false) {
      save(rows);
    }
  };

  const handleSaveClick = () => {
    const tableData = vetRef.current?.getData();
    const rows = (tableData?.rows as ILink[]) ?? [];
    if (!isDisabled(rows)) {
      save(rows);
    }
  };

  if (!editedLinks) return null;

  return (
    <Modal opened={isOpen} onClose={handleCloseModal} title={"Håndtere Lenker"} size="60vw" yOffset={140}>
      <Box>
        <ViewEditTable<ILink>
          columns={columns}
          data={editedLinks}
          onSave={() => {}}
          onRowsChange={(rows) => {
            setSaveDisabled(isDisabled(rows));
          }}
          newItemTemplateObject={newItemTemplateObject}
          canEdit={true}
          alwaysInEditMode={true}
          ref={vetRef}
          useIndexForDragOrder={true}
        />

        <VSpace />

        <div className="d-flex">
          <div className="flex-fill" />

          <Button color="gray" variant="outline" onClick={toggle}>
            Lukk
          </Button>
          <HSpace />
          <Button variant="filled" color="mc-primary" onClick={handleSaveClick} disabled={saveDisabled}>
            <i className="fa fa-floppy-o fa-fw" />
          </Button>
        </div>
      </Box>
    </Modal>
  );
};
